import {
  type ReactNode,
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { datadogRum } from "@datadog/browser-rum";
import { css, Global } from "@emotion/react";
import { Box, Column, useMergeRefs } from "@hightouch/htui";
import { Navigate, NoContextOutlet, useLocation } from "src/router";

import { Nav } from "src/components/layout/nav/nav";
import {
  MINI_NAV_PAGE_INSET,
  MiniNavProvider,
} from "src/components/layout/nav/mini-nav/mini-nav";
import { useIsMiniNavEligible } from "src/components/layout/nav/mini-nav/use-is-mini-nav-eligible";
import { useSidebarToggleMode } from "src/components/layout/nav/mini-nav/sidebar-toggle-route-handle";
import { useIsFullscreenRoute } from "src/components/layout/fullscreen-route";
import { useIsSettingsUrl } from "src/components/layout/nav/sections/hooks/projects";
import { SettingsModalProvider } from "src/components/layout/settings-modal-provider";
import { TimingMark } from "src/components/layout/timing-mark";
import {
  PageScrollProvider,
  usePageKeyboardScroll,
  usePageScrollRegion,
  WINDOW_PAGE_SCROLL,
} from "src/components/layout/page-scroll";
import { useIsAgenticSurfaceActive } from "src/hooks/use-agentic-home-enabled";
import { useUser } from "src/contexts/user-context";
import { saveIsProductNav, useNavWidth } from "src/contexts/nav-width-context";
import { SOFT_PAGE_BACKGROUND } from "src/ui/agentic-surfaces";
import { PageContentSkeleton } from "src/components/loading";
import { ErrorBoundary } from "src/components/error-boundary";
import { ImpersonationOverlay } from "src/components/layout/impersonation-overlay";
import { SpaceAccessGuard } from "src/components/spaces/space-access-guard";
import { AiFeaturesAnnouncementDialog } from "src/components/modals/ai-features-announcement-dialog";
import { ScopedAgentsTermsOfService } from "src/pages/agents/components/agents-tos";
import {
  MAIN_CONTENT_ID,
  SkipToContentLink,
} from "src/components/layout/skip-to-content";

export const Layout = ({
  children = null,
  hasRouteError = false,
}: {
  children?: ReactNode;
  /** The page failed to load, so the nav stays as the way out even where the page would hide it. */
  hasRouteError?: boolean;
}) => {
  const { user, isLoading, isCompanyRequired } = useUser();
  const location = useLocation();
  const isAgenticSurfaceActive = useIsAgenticSurfaceActive();
  const isSettingsUrl = useIsSettingsUrl();

  const navTimingFired = useRef(false);
  const [hasCrashed, setHasCrashed] = useState(false);
  // Layout owns the nav: a route declares the whole window, and a failed page keeps the nav as the way out.
  const isNavHidden = useIsFullscreenRoute() && !hasRouteError && !hasCrashed;
  const { isNavOpen } = useNavWidth();
  const isMiniNavEligible = useIsMiniNavEligible();
  // Derived here, not read back from the nav width context, so the first render already leaves the rail out.
  const isMiniNav = isMiniNavEligible && !isNavOpen && !isNavHidden;
  const pageRef = useRef<HTMLDivElement>(null);
  // Beside the product nav the window never scrolls: a page card scrolls in its place, or this column for a page without one.
  const isWindowScroll = !isMiniNavEligible || isNavHidden;
  const pageScroll = usePageScrollRegion("layout");
  usePageKeyboardScroll(!isWindowScroll);
  const pageColumnRef = useMergeRefs(
    pageRef,
    isWindowScroll ? undefined : pageScroll.ref,
  );
  const sidebarToggleMode = useSidebarToggleMode();

  useEffect(() => {
    if (user && !navTimingFired.current) {
      navTimingFired.current = true;
      // Timed from page load, so a nav that only appears after leaving a canvas would skew it.
      if (!isNavHidden) datadogRum.addTiming("nav_rendered");
    }
  }, [user, isNavHidden]);

  if (isLoading) {
    return <PageContentSkeleton />;
  }

  if (!user) {
    return (
      <Navigate
        state={location?.state}
        to={`/login?returnTo=${encodeURI(location?.pathname)}`}
      />
    );
  }

  if (isCompanyRequired) {
    return (
      <Navigate
        state={location?.state}
        to={`/restricted?returnTo=${encodeURI(location?.pathname)}`}
      />
    );
  }

  const page = (
    <ErrorBoundary onCrash={() => setHasCrashed(true)}>
      <Suspense fallback={<PageContentSkeleton />}>
        <TimingMark name="route_chunk_loaded" />
        <SpaceAccessGuard>
          <NoContextOutlet />
          {children}
          <ScopedAgentsTermsOfService />
          <AiFeaturesAnnouncementDialog />
        </SpaceAccessGuard>
      </Suspense>
    </ErrorBoundary>
  );

  return (
    <SettingsModalProvider>
      <ImpersonationOverlay />
      <SkipToContentLink />
      <PublishNavHidden isHidden={isNavHidden} />
      <PublishMiniNav isEligible={isMiniNavEligible} />
      <ShowPageGroundBehindFold isShown={isMiniNavEligible && !isNavHidden} />
      {/* The mini-nav renders its own sidebar, docked or collapsed, ahead of the page in this row. */}
      <MiniNavProvider
        isEligible={isMiniNavEligible && !isNavHidden}
        hasCrumbBars={
          isMiniNavEligible && !isNavHidden && sidebarToggleMode !== "hidden"
        }
        isActive={isMiniNav && sidebarToggleMode !== "hidden"}
        pageRef={pageRef}
      >
        {!isNavHidden && !isMiniNavEligible && <Nav />}
        {/* The docked sidebar is what made the row a window tall; without it, contained page cards would shrink to their content. */}
        {isMiniNav && <Box aria-hidden flexShrink={0} minHeight="100vh" />}
        {!isWindowScroll && <Global styles={noWindowScroll} />}
        {/* The collapsed sidebar takes no width, so the page card keeps the gutter the sidebar gave it. */}
        <Column
          ref={pageColumnRef}
          as="main"
          id={MAIN_CONTENT_ID}
          // The skip link lands focus here, which draws no ring on the whole page.
          tabIndex={-1}
          _focus={{ outline: "none" }}
          bg="base.background"
          flex={1}
          minWidth={0}
          pl={isMiniNav ? MINI_NAV_PAGE_INSET : undefined}
          // The visible viewport, which a phone's toolbars can make shorter than 100vh.
          height={isWindowScroll ? undefined : "100dvh"}
          overflowY={isWindowScroll ? undefined : "auto"}
          overscrollBehavior={isWindowScroll ? undefined : "none"}
          // Docked too, so the card's right gutter matches its top one in both states and holds still through the fold.
          pr={
            isMiniNavEligible && !isNavHidden ? MINI_NAV_PAGE_INSET : undefined
          }
        >
          {/* Agentic surfaces paint this gradient from a lazily-loaded chunk;
              painting it here too keeps the transparent nav from ever having the
              white document body behind it while that chunk loads. */}
          {isAgenticSurfaceActive && (
            <Box
              aria-hidden
              position="fixed"
              inset={0}
              zIndex={0}
              pointerEvents="none"
              background={SOFT_PAGE_BACKGROUND}
            />
          )}
          {/* A settings URL opens in the settings modal over home, so its page never renders here. */}
          {!isSettingsUrl && (
            <PageScrollProvider
              value={isWindowScroll ? WINDOW_PAGE_SCROLL : pageScroll.value}
            >
              {page}
            </PageScrollProvider>
          )}
        </Column>
      </MiniNavProvider>
    </SettingsModalProvider>
  );
};

// No gutter either: the window has nothing to scroll, so a reserved one would only widen the card's right gutter.
const noWindowScroll = css`
  html {
    overflow: hidden;
    scrollbar-gutter: auto;
  }
`;

/**
 * Clears the body's white, so the root's page-gray ground shows through any gap the sidebar's fold opens beside the page.
 * The root paints `base.background`; the body's own background would otherwise cover anything layered behind it.
 */
const ShowPageGroundBehindFold = ({ isShown }: { isShown: boolean }) => {
  useLayoutEffect(() => {
    if (!isShown) return;
    document.body.style.setProperty("background-color", "transparent");
    return () => {
      document.body.style.removeProperty("background-color");
    };
  }, [isShown]);
  return null;
};

/** Publishes whether this viewer's nav is the mini-nav, before paint, so the page never draws a frame beside a rail. */
const PublishMiniNav = ({ isEligible }: { isEligible: boolean }) => {
  const { setIsMiniNavEligible } = useNavWidth();
  useLayoutEffect(() => {
    setIsMiniNavEligible(isEligible);
    saveIsProductNav(isEligible);
    return () => setIsMiniNavEligible(false);
  }, [isEligible, setIsMiniNavEligible]);
  return null;
};

/** Publishes Layout's decision to the nav's width context, which sits above the routes; only this re-renders when it changes. */
const PublishNavHidden = ({ isHidden }: { isHidden: boolean }) => {
  const { setIsNavHidden } = useNavWidth();
  // Before paint, so nothing that offsets by the nav's width draws a frame at the old width.
  useLayoutEffect(() => {
    setIsNavHidden(isHidden);
    return () => setIsNavHidden(false);
  }, [isHidden, setIsNavHidden]);
  return null;
};
